/**
 * Bot Accounts API
 *
 * Wraps /api/bots — the Twitch accounts PratBot runs as, and the per-user
 * "bot override" that substitutes one of them for the system bot (PratPerch)
 * across the channels that user owns.
 *
 * Consumed by:
 *   - pages/AdminBots.tsx     (super-admin catalogue)
 *   - pages/BotOverride.tsx   (the user-facing connect/disconnect page)
 *
 * Two things here are load-bearing and easy to break; both are commented at
 * their definitions: openRegistrationPopup() must stay synchronous (and is
 * followed by startRegistration()), and registration does NOT set the override.
 */

import api from '@/api/client'

/** A Twitch account PratBot can speak as. Never carries credentials. */
export interface BotAccount {
  id: number
  /** Owning PratBot user's DB id. null = the system bot (PratPerch). */
  owner_id: number | null
  /** Owner's Twitch name, for display. null for the system bot. */
  owner_username: string | null
  twitch_id: string
  twitch_username: string
  is_system_default: boolean
  is_active: boolean
  is_token_expired: boolean
  /** ChannelBot assignment rows. NOT the number of channels an override serves. */
  channel_count: number
  /**
   * Whether the CALLER owns this bot, resolved server-side.
   *
   * Necessary because the frontend can't work it out: the JWT carries the
   * Twitch id, while owner_id refers to the internal users.id. A super admin's
   * list also includes every other user's bots, and PUT /api/bots/active still
   * requires ownership — so this is what keeps the override page from offering
   * a bot that would 404.
   */
  is_mine: boolean
}

/** GET /api/bots — the system bot plus the caller's own bots. */
export interface BotAccountList {
  system_bot: BotAccount | null
  user_bots: BotAccount[]
}

/**
 * What the registration popup posts back to the opener.
 *
 * The popup renders this from the callback route and posts it to the frontend
 * origin (never "*"). The receiver MUST still verify event.origin — see
 * useBotRegistrationListener below.
 */
export interface BotRegisteredMessage {
  type: 'bot_registered'
  ok: boolean
  /** true = a new BotAccount row; false = an existing one was re-authorised. */
  created?: boolean
  bot?: {
    id: number
    twitch_id: string
    twitch_username: string
  }
  error?: string
  message?: string
}

/**
 * Narrow an untyped postMessage payload.
 *
 * A window receives messages from anything that can reach it — extensions,
 * embedded frames, other tabs — so shape-checking is not optional even after
 * the origin check.
 */
export function isBotRegisteredMessage(
  data: unknown,
): data is BotRegisteredMessage {
  if (typeof data !== 'object' || data === null) return false
  const candidate = data as Record<string, unknown>
  return candidate.type === 'bot_registered' && typeof candidate.ok === 'boolean'
}

/** POST /api/bots/register/start */
export interface RegistrationStart {
  /** Twitch's authorize URL, carrying a state bound to the caller. */
  authorize_url: string
}

/**
 * Open the Twitch bot-authorisation popup — BLANK.
 *
 * MUST be called SYNCHRONOUSLY from the click handler. Popup blockers only
 * honour a window.open() that is still inside the user-gesture task, so any
 * `await` before this call — even one that resolves immediately — gets the
 * popup killed. So the popup opens empty here, and startRegistration() gives
 * it its destination once the authenticated POST returns.
 *
 * This used to open `/api/bots/register/start?token=<JWT>` directly, which put
 * a full JWT in the server journal and the popup's history (R22). No
 * credential is in any URL now.
 *
 * Returns the popup handle, or null if it was blocked.
 */
export function openRegistrationPopup(): Window | null {
  return window.open(
    '',
    'pratbot_bot_auth',
    'width=520,height=760,menubar=no,toolbar=no,location=no',
  )
}

/** One of the caller's channels, and whether their bot can moderate it. */
export interface BotModStatusEntry {
  channel_id: number
  twitch_channel_name: string
  is_moderator: boolean
}

/**
 * GET /api/bots/active/mod-status — live Twitch state, not stored.
 *
 * `checked: false` means Twitch could not be asked, and `channels` is empty as
 * a result. That is NOT "no channel is modded": treating the two the same would
 * warn people about a problem they may not have. The usual cause is a bot
 * authorised before `user:read:moderated_channels` was requested.
 */
export interface BotModStatus {
  checked: boolean
  reason: string | null
  bot_username: string | null
  channels: BotModStatusEntry[]
}

/** POST /api/bots/{id}/refresh */
export interface BotRefreshResult {
  status: string
  message: string
  expires_at?: string | null
}

export const botsApi = {
  /** The system bot plus the caller's own bots (super admins see everyone's). */
  list(): Promise<BotAccountList> {
    return api.get<BotAccountList>('/api/bots')
  },

  /** The system default bot. Readable by any authenticated user. */
  getSystem(): Promise<BotAccount> {
    return api.get<BotAccount>('/api/bots/system')
  },

  get(botId: number): Promise<BotAccount> {
    return api.get<BotAccount>(`/api/bots/${botId}`)
  },

  /**
   * Delete a bot account. Cascades to its ChannelBot assignments, and the
   * owner's override reverts to the system bot (FK is ON DELETE SET NULL).
   *
   * Not the same as clearActive(): this destroys the registration.
   */
  remove(botId: number): Promise<void> {
    return api.delete<void>(`/api/bots/${botId}`)
  },

  /**
   * The caller's override, or null when their channels run on the system bot.
   *
   * Returns 200 with a null body, so it doubles as the polling backstop for a
   * popup whose window.opener was stripped (COOP, mobile Safari).
   */
  getActive(): Promise<BotAccount | null> {
    return api.get<BotAccount | null>('/api/bots/active')
  },

  /**
   * Set the override. Only a bot the caller OWNS is accepted; anything else
   * 404s, including the system bot.
   *
   * Registration does not call this — the user confirms afterwards, which is
   * the last checkpoint against having authorised the wrong Twitch account.
   */
  setActive(botAccountId: number): Promise<BotAccount> {
    return api.put<BotAccount>('/api/bots/active', {
      bot_account_id: botAccountId,
    })
  },

  /** Revert to the system bot. The BotAccount row survives. */
  clearActive(): Promise<void> {
    return api.delete<void>('/api/bots/active')
  },

  /**
   * Whether the override bot is /mod-ded in the channels it serves.
   *
   * Its own endpoint rather than a field on the channel list because it costs a
   * Twitch round trip — only worth paying where the answer is shown.
   */
  getModStatus(): Promise<BotModStatus> {
    return api.get<BotModStatus>('/api/bots/active/mod-status')
  },

  /**
   * Revalidate a bot's stored credentials with Twitch, refreshing them if the
   * access token has gone stale, and hand the result to the running client.
   *
   * Rarely needed: tokens refresh on their own schedule. Useful when something
   * looks wrong and you want to know whether the credentials are the problem.
   * 502 means Twitch rejected them and the bot needs re-authorising; 503 means
   * the bot process is not running.
   */
  /**
   * Begin registration in an already-open popup (see openRegistrationPopup).
   *
   * POSTs with the normal Authorization header, then navigates the popup to
   * Twitch. On failure the popup is closed and the error rethrown, so the
   * caller can toast it with apiErrorMessage.
   */
  async startRegistration(popup: Window): Promise<void> {
    try {
      const { authorize_url } = await api.post<RegistrationStart>(
        '/api/bots/register/start',
      )
      popup.location.href = authorize_url
    } catch (err) {
      popup.close()
      throw err
    }
  },

  refresh(botId: number): Promise<BotRefreshResult> {
    return api.post<BotRefreshResult>(`/api/bots/${botId}/refresh`)
  },
}

export default botsApi
